// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Per-instance and per-room geometry, folded client-side from the streamed
 * pages: centroid, point count, bounding box, and which rooms an instance's
 * points fall in. The server now answers both (`GET /rooms`,
 * `InstanceInfo.centroid/bbox`, ruxd-6tz) and wins where it does
 * (`bygning/spatial.ts`); this index fills in on an older server and for
 * anything it does not answer — the Ground needs a click target per part and
 * a box to frame per room, and the pages it already downloads carry both.
 *
 * Accumulated per scene layer, so the coarse overview's contribution can be
 * dropped whole when the full-resolution pages supersede it (the same rule
 * the scene follows), and a restarted stream (`clouds.changed`, a new label
 * set, a new Tæthed) replaces rather than double-counts.
 *
 * Coordinates are world (as stored, before the scene's recentring). Sums are
 * kept in doubles. Pure: no three.js, no DOM.
 */

/** One accumulated extent: a running sum, a count and a box. */
interface Acc {
  sx: number;
  sy: number;
  sz: number;
  n: number;
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

interface InstanceAcc extends Acc {
  /** room id → points of this instance in it (room 0 = none). */
  rooms: Map<number, number>;
}

interface LayerAcc {
  instances: Map<number, InstanceAcc>;
  rooms: Map<number, Acc>;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** What the index knows about one labelled extent (an instance or a room). */
export interface ExtentStats {
  id: number;
  count: number;
  centroid: Vec3;
  min: Vec3;
  max: Vec3;
}

export interface InstanceStats extends ExtentStats {
  /** The room most of its points fall in, or null when none are in a room. */
  room: number | null;
  /** room id → point count (room 0, "no room", is left out). */
  rooms: Map<number, number>;
}

/** A label value the contract says is not a class: 0 (unlabeled) or a wrapped -1 (STANDARDS §3.1). */
function isClass(label: number): boolean {
  return label > 0 && label <= 0x7fffffff;
}

function emptyAcc(): Acc {
  return {
    sx: 0,
    sy: 0,
    sz: 0,
    n: 0,
    minX: Infinity,
    minY: Infinity,
    minZ: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
    maxZ: -Infinity,
  };
}

function grow(acc: Acc, x: number, y: number, z: number): void {
  acc.sx += x;
  acc.sy += y;
  acc.sz += z;
  acc.n += 1;
  if (x < acc.minX) acc.minX = x;
  if (y < acc.minY) acc.minY = y;
  if (z < acc.minZ) acc.minZ = z;
  if (x > acc.maxX) acc.maxX = x;
  if (y > acc.maxY) acc.maxY = y;
  if (z > acc.maxZ) acc.maxZ = z;
}

function merge(into: Acc, from: Acc): void {
  into.sx += from.sx;
  into.sy += from.sy;
  into.sz += from.sz;
  into.n += from.n;
  into.minX = Math.min(into.minX, from.minX);
  into.minY = Math.min(into.minY, from.minY);
  into.minZ = Math.min(into.minZ, from.minZ);
  into.maxX = Math.max(into.maxX, from.maxX);
  into.maxY = Math.max(into.maxY, from.maxY);
  into.maxZ = Math.max(into.maxZ, from.maxZ);
}

function stats(id: number, acc: Acc): ExtentStats {
  return {
    id,
    count: acc.n,
    centroid: { x: acc.sx / acc.n, y: acc.sy / acc.n, z: acc.sz / acc.n },
    min: { x: acc.minX, y: acc.minY, z: acc.minZ },
    max: { x: acc.maxX, y: acc.maxY, z: acc.maxZ },
  };
}

export class InstanceIndex {
  private readonly layers = new Map<string, LayerAcc>();
  private cache: { revision: number; instances: Map<number, InstanceStats>; rooms: Map<number, ExtentStats> } | null =
    null;
  /** Bumped on every change; a caller compares it to know when to re-read. */
  revision = 0;

  /**
   * Fold one page in. `instances` and `rooms` are index-aligned with
   * `positions` (xyz stride 3); either may be null (no such label cloud).
   * Points with label 0 count for nothing on that axis.
   */
  addPage(layer: string, positions: Float32Array, instances: Uint32Array | null, rooms: Uint32Array | null): void {
    const count = Math.floor(positions.length / 3);
    const inst = instances && instances.length >= count ? instances : null;
    const room = rooms && rooms.length >= count ? rooms : null;
    if (!inst && !room) return;

    let acc = this.layers.get(layer);
    if (!acc) {
      acc = { instances: new Map(), rooms: new Map() };
      this.layers.set(layer, acc);
    }

    // Labels come in runs (a page is spatially coherent), so the last
    // accumulator is reused rather than looked up per point.
    let lastInst = -1;
    let instAcc: InstanceAcc | undefined;
    let lastRoom = -1;
    let roomAcc: Acc | undefined;

    for (let i = 0; i < count; i += 1) {
      const x = positions[i * 3];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];
      const r = room ? room[i] : 0;
      const roomId = isClass(r) ? r : 0;

      if (roomId !== 0) {
        if (roomId !== lastRoom) {
          lastRoom = roomId;
          roomAcc = acc.rooms.get(roomId);
          if (!roomAcc) {
            roomAcc = emptyAcc();
            acc.rooms.set(roomId, roomAcc);
          }
        }
        grow(roomAcc!, x, y, z);
      }

      if (inst) {
        const id = inst[i];
        if (!isClass(id)) continue;
        if (id !== lastInst) {
          lastInst = id;
          instAcc = acc.instances.get(id);
          if (!instAcc) {
            instAcc = { ...emptyAcc(), rooms: new Map() };
            acc.instances.set(id, instAcc);
          }
        }
        grow(instAcc!, x, y, z);
        if (roomId !== 0) instAcc!.rooms.set(roomId, (instAcc!.rooms.get(roomId) ?? 0) + 1);
      }
    }
    this.revision += 1;
  }

  /** Drop everything one layer contributed (the superseded overview, a restarted stream). */
  removeLayer(layer: string): void {
    if (this.layers.delete(layer)) this.revision += 1;
  }

  clear(): void {
    if (this.layers.size === 0) return;
    this.layers.clear();
    this.revision += 1;
  }

  private merged() {
    if (this.cache && this.cache.revision === this.revision) return this.cache;
    const inst = new Map<number, InstanceAcc>();
    const rooms = new Map<number, Acc>();
    for (const layer of this.layers.values()) {
      for (const [id, a] of layer.instances) {
        let into = inst.get(id);
        if (!into) {
          into = { ...emptyAcc(), rooms: new Map() };
          inst.set(id, into);
        }
        merge(into, a);
        for (const [r, n] of a.rooms) into.rooms.set(r, (into.rooms.get(r) ?? 0) + n);
      }
      for (const [id, a] of layer.rooms) {
        let into = rooms.get(id);
        if (!into) {
          into = emptyAcc();
          rooms.set(id, into);
        }
        merge(into, a);
      }
    }
    const instances = new Map<number, InstanceStats>();
    for (const [id, a] of inst) {
      if (a.n === 0) continue;
      let best: number | null = null;
      let most = 0;
      for (const [r, n] of a.rooms) {
        if (n > most || (n === most && best !== null && r < best)) [best, most] = [r, n];
      }
      instances.set(id, { ...stats(id, a), room: best, rooms: a.rooms });
    }
    const roomStats = new Map<number, ExtentStats>();
    for (const [id, a] of rooms) if (a.n > 0) roomStats.set(id, stats(id, a));
    this.cache = { revision: this.revision, instances, rooms: roomStats };
    return this.cache;
  }

  /** Every instance with at least one point, by id. */
  instances(): Map<number, InstanceStats> {
    return this.merged().instances;
  }

  /** Every room with at least one point, by id. */
  rooms(): Map<number, ExtentStats> {
    return this.merged().rooms;
  }

  /** Centroids for `ids` as xyz stride 3; `NaN` for an id the index has not seen. */
  centroids(ids: readonly number[]): Float32Array {
    const all = this.instances();
    const out = new Float32Array(ids.length * 3).fill(Number.NaN);
    ids.forEach((id, i) => {
      const s = all.get(id);
      if (!s) return;
      out[i * 3] = s.centroid.x;
      out[i * 3 + 1] = s.centroid.y;
      out[i * 3 + 2] = s.centroid.z;
    });
    return out;
  }
}

/**
 * Nearest screen point (xy stride 2) to `(x, y)` within `radiusPx`, or null.
 * `NaN` entries (off screen, clipped, unknown) are skipped; a tie keeps the
 * earlier index. Linear — fine for the few hundred click targets a building has.
 */
export function hitTest(screen: Float32Array, x: number, y: number, radiusPx: number): number | null {
  let best: number | null = null;
  let bestD = radiusPx * radiusPx;
  const n = Math.floor(screen.length / 2);
  for (let i = 0; i < n; i += 1) {
    const sx = screen[i * 2];
    const sy = screen[i * 2 + 1];
    if (Number.isNaN(sx) || Number.isNaN(sy)) continue;
    const dx = sx - x;
    const dy = sy - y;
    const d = dx * dx + dy * dy;
    if (d <= bestD && (best === null || d < bestD)) {
      best = i;
      bestD = d;
    }
  }
  return best;
}
